import { Section, SectionHeading } from "@components/shared/Section";
import { Product, ProductIcon } from "@components/shared/ProductIcon";
import { AUTO_LOAN, MORTGAGE, UNLOCK } from "@/lib/site";
import { cn } from "@/ui/utils/cn";
import { useTranslations } from "@services/strings";
import { ArrowRight } from "lucide-react";
import Link from "next/link";

type Row = {
  product: Product;
  href: string;
  name: string;
  promise: string;
  facts: string;
  cta: string;
  /** Primary rows get the large treatment; secondary ones sit quieter. */
  primary: boolean;
};

/** The large and the quieter treatment, picked once per row. */
const TREATMENT = {
  primary: {
    link: "p-6 md:p-8",
    name: "text-2xl md:text-3xl",
    // Below md the CTA sits under the text, so indent it past the icon.
    cta: "ps-[3.75rem]",
  },
  secondary: {
    link: "p-5 md:px-8 md:py-6",
    name: "text-xl md:text-2xl",
    cta: "ps-[3.75rem]",
  },
} as const;

const ProductRow = ({ row }: { row: Row }) => {
  const treatment = TREATMENT[row.primary ? "primary" : "secondary"];
  return (
    <li>
      <Link
        href={row.href}
        className={cn(
          "group grid items-center gap-x-10 gap-y-4 rounded-3xl bg-white transition-shadow hover:shadow-panel md:grid-cols-[minmax(0,1fr)_auto]",
          treatment.link,
        )}
      >
        <div className="flex items-start gap-5">
          <ProductIcon product={row.product} />
          <div className="min-w-0">
            <h3 className={cn("display", treatment.name)}>{row.name}</h3>
            <p className="mt-1.5 max-w-prose text-body text-slate-700">
              {row.promise}
            </p>
            <p className="mt-2 text-sm text-slate-500">{row.facts}</p>
          </div>
        </div>
        <span
          className={cn(
            "inline-flex items-center gap-2 text-[15px] font-semibold text-ink md:justify-self-end md:ps-0",
            treatment.cta,
          )}
        >
          {row.cta}
          <ArrowRight
            size={18}
            className="transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1"
            aria-hidden="true"
          />
        </span>
      </Link>
    </li>
  );
};

/**
 * The product list. Not a card grid: a ledger of four rows where the two
 * property products lead and mortgage/auto follow at a smaller size.
 */
export const Products = () => {
  const { t, formatNumber, formatPercent } = useTranslations();

  const rows: Row[] = [
    {
      product: "unlock",
      href: "/unlock",
      name: t("site.home.products.unlock.name"),
      promise: t("site.home.products.unlock.promise"),
      facts: t("site.home.products.unlock.facts", {
        ltv: formatPercent(UNLOCK.maxLoanToValue),
        maxAmount: formatNumber(UNLOCK.maxAmount),
        maxYears: UNLOCK.maxYears,
      }),
      cta: t("site.home.products.unlock.cta"),
      primary: true,
    },
    {
      product: "payLess",
      href: "/pay-less",
      name: t("site.home.products.payLess.name"),
      promise: t("site.home.products.payLess.promise"),
      facts: t("site.home.products.payLess.facts"),
      cta: t("site.home.products.payLess.cta"),
      primary: true,
    },
    {
      product: "mortgage",
      href: "/mortgage",
      name: t("site.home.products.mortgage.name"),
      promise: t("site.home.products.mortgage.promise"),
      facts: t("site.home.products.mortgage.facts", {
        ltv: formatPercent(MORTGAGE.maxLoanToValue),
        maxYears: MORTGAGE.maxYears,
      }),
      cta: t("site.home.products.mortgage.cta"),
      primary: false,
    },
    {
      product: "auto",
      href: "/auto-loans",
      name: t("site.home.products.auto.name"),
      promise: t("site.home.products.auto.promise"),
      facts: t("site.home.products.auto.facts", {
        maxAmount: formatNumber(AUTO_LOAN.maxAmount),
        maxYears: AUTO_LOAN.maxYears,
      }),
      cta: t("site.home.products.auto.cta"),
      primary: false,
    },
  ];

  return (
    <Section tone="mist" id="products">
      <SectionHeading
        title={t("site.home.products.title")}
        lead={t("site.home.products.intro")}
      />
      <ul className="mt-12 flex flex-col gap-4">
        {rows.map((row) => (
          <ProductRow key={row.href} row={row} />
        ))}
      </ul>
    </Section>
  );
};
